import { lazy, Suspense, useId, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, toApiError, unwrap, type Schemas } from "../../api/client";
import { useAttribution } from "../../components/Attribution";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { ErrorMessage, Loading, Notice } from "../../components/ui";
import { t } from "../../i18n/t";
import { formatDateTime } from "../../lib/format";
import { routes } from "../../lib/routes";
import { usePageTitle } from "../../lib/usePageTitle";
import { useQuery } from "../../lib/useQuery";
import type { Point } from "../../map/FauMapView";
const FauMapView = lazy(() => import("../../map/FauMapView"));
interface Finding {
  key: string;
  title: string;
  location: string;
}
function findings(campaign: Schemas["PublicCampaign"]): Finding[] {
  const features: unknown = campaign.findings.features;
  if (!Array.isArray(features)) return [];
  return features.flatMap((f: unknown) => {
    const p = (f as { properties?: Record<string, unknown> } | null)?.properties;
    return p && typeof p.key === "string" && typeof p.title === "string"
      ? [{ key: p.key, title: p.title, location: typeof p.location === "string" ? p.location : "" }]
      : [];
  });
}
function CampaignForm({ campaign, token }: { campaign: Schemas["PublicCampaign"]; token: string }) {
  const id = useId();
  const [selection, setSelection] = useState<Finding | "new" | null>(null);
  const [point, setPoint] = useState<Point | null>(null);
  const [category, setCategory] = useState("");
  const [comment, setComment] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [thanks, setThanks] = useState("");
  const [closed, setClosed] = useState(false);
  const items = findings(campaign);
  function choose(value: Finding | "new" | null) {
    setSelection(value);
    setPoint(null);
    setCategory(value && value !== "new" ? "annet" : "");
    setComment("");
    setError(null);
    setThanks("");
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selection || busy) return;
    setError(null);
    if (selection === "new" && !point) {
      setError(new ApiError(422, t("fauPublic.pointMissing")));
      return;
    }
    if (!category) {
      setError(new ApiError(422, t("fauPublic.categoryMissing")));
      return;
    }
    if ([...comment].length > 500) {
      setError(new ApiError(422, t("fauPublic.commentTooLong")));
      return;
    }
    setBusy(true);
    try {
      const result = await unwrap(
        api.POST("/api/fau/{token}/reports", {
          params: { path: { token } },
          body: {
            kind: selection === "new" ? "new" : "confirm",
            ...(selection === "new" ? point : { finding_key: selection.key }),
            category,
            comment: comment.trim() || null,
            website,
          },
        }),
      );
      setThanks(result.detail);
      setSelection(null);
      setComment("");
      setPoint(null);
      setCategory("");
      setWebsite("");
    } catch (err) {
      const failure = toApiError(err);
      setError(failure);
      if (failure.status === 404 || failure.status === 410) setClosed(true);
    } finally {
      setBusy(false);
    }
  }
  if (closed)
    return (
      <Notice role="alert">
        <h2>{t("fauPublic.closedTitle")}</h2>
        <p>{error?.userMessage}</p>
      </Notice>
    );
  if (thanks)
    return (
      <section className="card" role="status">
        <h2>{t("fauPublic.thanksTitle")}</h2>
        <p>{thanks}</p>
        <button type="button" className="btn btn-primary" onClick={() => choose(null)}>
          {t("fauPublic.again")}
        </button>
      </section>
    );
  return (
    <>
      <ErrorBoundary
        fallback={
          <Notice>
            <p>{t("fauPublic.mapUnavailable")}</p>
          </Notice>
        }
      >
        <Suspense fallback={<Loading label={t("fauPublic.loading")} />}>
          <FauMapView
            campaign={campaign}
            placing={selection === "new"}
            point={point}
            onPlace={setPoint}
            onSelect={(key) => {
              const item = items.find((f) => f.key === key);
              if (item) choose(item);
            }}
          />
        </Suspense>
      </ErrorBoundary>
      <button
        type="button"
        className="btn btn-primary"
        disabled={busy || campaign.bbox.length !== 4}
        onClick={() => choose("new")}
      >
        {t("fauPublic.newPoint")}
      </button>
      {selection && (
        <form
          className="card section"
          onSubmit={(event) => void submit(event)}
          noValidate
          aria-labelledby={`${id}-title`}
        >
          <h2 id={`${id}-title`}>
            {t(selection === "new" ? "fauPublic.newTitle" : "fauPublic.confirmTitle")}
          </h2>
          {selection === "new" ? (
            <>
              <p>{t("fauPublic.placeHint")}</p>
              <p role="status">
                {point ? t("fauPublic.pointPlaced") : t("fauPublic.pointMissing")}
              </p>
            </>
          ) : (
            <p>
              {t("fauPublic.selected", { title: selection.title, location: selection.location })}
            </p>
          )}
          <div className="field">
            <label htmlFor={`${id}-category`}>{t("fauPublic.category")}</label>
            <select
              id={`${id}-category`}
              value={category}
              required
              disabled={busy}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">{t("fauPublic.categoryMissing")}</option>
              {campaign.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor={`${id}-comment`}>{t("fauPublic.comment")}</label>
            <textarea
              id={`${id}-comment`}
              value={comment}
              disabled={busy}
              maxLength={500}
              rows={4}
              aria-describedby={`${id}-warning ${id}-counter`}
              onChange={(e) => setComment(e.target.value)}
            />
            <p id={`${id}-warning`} className="hint">
              {t("fauPublic.commentWarning")}
            </p>
            <span id={`${id}-counter`}>
              {t("fauPublic.counter", { count: [...comment].length, max: 500 })}
            </span>
          </div>
          <div className="fau-honeypot" aria-hidden="true">
            <label htmlFor={`${id}-website`}>{t("fauPublic.honeypot")}</label>
            <input
              id={`${id}-website`}
              name="website"
              value={website}
              tabIndex={-1}
              autoComplete="off"
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          {error && <ErrorMessage error={error} />}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {t(busy ? "fauPublic.sending" : "fauPublic.submit")}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => choose(null)}
            >
              {t("fauPublic.cancel")}
            </button>
          </div>
        </form>
      )}
      <section className="section">
        <h2>{t("fauPublic.findingsTitle")}</h2>
        {items.length === 0 && <p>{t("fauPublic.findingsEmpty")}</p>}
        <ul className="plain-list finding-list">
          {items.map((f) => (
            <li key={f.key}>
              <h3>{f.title}</h3>
              <p>{f.location}</p>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                aria-label={t("fauPublic.confirmLabel", { title: f.title, location: f.location })}
                onClick={() => choose(f)}
              >
                {t("fauPublic.confirm")}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
export function FauPublicPage() {
  const { token = "" } = useParams();
  const query = useQuery(`fau:${token}`, (signal) =>
    unwrap(api.GET("/api/fau/{token}", { params: { path: { token } }, signal })),
  );
  usePageTitle(query.data?.title ?? t("nav.fau"));
  useAttribution(query.data?.attribution);
  if (query.error)
    return (
      <div className="page narrow">
        {query.error.status === 404 || query.error.status === 410 ? (
          <Notice>
            <h1>
              {t(query.error.status === 404 ? "fauPublic.notFoundTitle" : "fauPublic.revokedTitle")}
            </h1>
            <p>
              {query.error.status === 404 ? t("fauPublic.notFoundBody") : query.error.userMessage}
            </p>
          </Notice>
        ) : (
          <ErrorMessage error={query.error} onRetry={query.reload} />
        )}
      </div>
    );
  if (!query.data) return <Loading label={t("fauPublic.loading")} />;
  const campaign = query.data;
  return (
    <div className="page narrow">
      <header className="page-header">
        <h1>{campaign.title}</h1>
        <p>{t("fauPublic.kommune", { kommune: campaign.kommunenavn })}</p>
        <p>{t("fauPublic.schools", { schools: campaign.schools.map((s) => s.name).join(", ") })}</p>
        <p>{t("fauPublic.intro")}</p>
        <p>{t("fauPublic.openUntil", { date: formatDateTime(campaign.closes_at) })}</p>
      </header>
      <Notice>
        <p>{t("fauPublic.privacyShort")}</p>
        <Link to={routes.privacy()}>{t("fauPublic.privacyLink")}</Link>
      </Notice>
      {campaign.open ? (
        <CampaignForm key={token} campaign={campaign} token={token} />
      ) : (
        <Notice>
          <h2>{t("fauPublic.closedTitle")}</h2>
          <p>{campaign.message}</p>
        </Notice>
      )}
    </div>
  );
}
